import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
} from "@mui/material";

import {
  FieldCategory,
  FieldValueType,
} from "@safetyculture/s12-apis-connect-web/s12/typefield/v1/field_pb";
import {
  Field,
  FieldStatus,
  ListFieldsRequest,
  ListFieldsRequest_FieldsFilter,
} from "@safetyculture/s12-apis-connect-web/s12/users/userfield/v1/field_pb";

import { listFields } from "../../../api/endpoints/user-field";
import { callOptionsHeaders } from "../../../api/helpers";
import { GrpcError, SnackbarContext } from "../../../context/snackbar-context";
import GenericDialog from "../../shared/generic-dialog";
import EmptyTableResults from "../../shared/table/empty-table-results";
import { UserFieldsFilters } from "./user-fields-filters";
import { UserFieldsLoader } from "./user-fields-loader";
import { UserFieldsTableHeader } from "./user-fields-table-header";
import { UserFieldsTableRow } from "./user-fields-table-row";

type UserFieldsDialogProps = {
  open: boolean;
  setIsOpen: (isOpen: boolean) => void;
  roleId: string;
  regionCode: string;
};

export const UserFieldsDialog = (props: UserFieldsDialogProps) => {
  const { open, setIsOpen, roleId, regionCode } = props;
  const { showError } = useContext(SnackbarContext);
  const [fields, setFields] = useState<Field[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [status, setStatus] = useState<FieldStatus>(FieldStatus.ENABLED);
  const [category, setCategory] = useState<FieldCategory>(
    FieldCategory.UNSPECIFIED,
  );
  const [dataTypes, setDataTypes] = useState<FieldValueType[]>([]);
  const fetchUserFields = useCallback(async () => {
    try {
      setIsLoading(true);
      const req = new ListFieldsRequest({
        orgId: roleId,
        filter: new ListFieldsRequest_FieldsFilter({
          status,
          fieldCategory: category,
          dataTypes,
        }),
      });
      const fields = await listFields(
        req,
        callOptionsHeaders(regionCode, roleId),
      );
      if (fields) {
        setFields(fields);
      }
    } catch (e) {
      showError(e as GrpcError);
    } finally {
      setIsLoading(false);
    }
  }, [regionCode, roleId, showError, status, category, dataTypes]);

  useEffect(() => {
    if (open) {
      fetchUserFields();
    }
  }, [fetchUserFields, open]);
  return (
    <GenericDialog
      title="User Fields"
      width="xl"
      hasAckBtn={true}
      acknowledgeBtnLabel="OK"
      open={open}
      handleAckBtnClick={() => {
        setIsOpen(false);
      }}
      handleDialogClose={() => {
        setIsOpen(false);
      }}
      data-testid="user-fields-dialog"
    >
      <UserFieldsFilters
        status={status}
        setStatus={setStatus}
        category={category}
        setCategory={setCategory}
        dataTypes={dataTypes}
        setDataTypes={setDataTypes}
        disabled={isLoading}
      />
      <TableContainer
        style={{ maxHeight: "600px", overflow: "auto" }}
        component={Paper}
      >
        <Table stickyHeader>
          <TableHead>
            <UserFieldsTableHeader />
          </TableHead>
          <TableBody>
            {isLoading && <UserFieldsLoader />}
            {!isLoading && fields.length === 0 && (
              <EmptyTableResults
                data-testid="user-field-table-empty"
                colSpan={5}
                tableHeading="user fields in this organisation"
              />
            )}
            {!isLoading &&
              fields.map((field) => (
                <UserFieldsTableRow field={field} key={field.id} />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </GenericDialog>
  );
};
